import { useCallback, useEffect, useState } from "react";
import type { DateRangePreset, GeographyResponse } from "@fbc/shared";
import { apiFetch, ApiError } from "../lib/api.js";

/**
 * useGeography — fetch the geography metrics for a date range.
 *
 * Surfaces three exclusive states:
 *   - `loading`: request in flight.
 *   - `proRequired`: the backend returned 403 `{ error: "pro_required" }`.
 *     Callers should render an upgrade card, NOT an error banner.
 *   - `error`: any other failure (network, server error, etc.).
 *
 * `proRequired` and `error` are mutually exclusive — when the API returns the
 * Pro gate response, `error` stays null so the UI can branch cleanly.
 */
export function useGeography(preset: DateRangePreset) {
  const [data, setData] = useState<GeographyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proRequired, setProRequired] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setProRequired(false);
    try {
      const result = await apiFetch<GeographyResponse>(
        `/api/metrics/geography?preset=${encodeURIComponent(preset)}`,
      );
      setData(result);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403 && e.code === "pro_required") {
        setProRequired(true);
        setData(null);
      } else {
        const message =
          e instanceof ApiError ? e.message : "Could not load geography data.";
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [preset]);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, proRequired, reload: load };
}
